/**
 * Shopify Attribution Utilities
 * 
 * Handles visitor tracking and cart attribution:
 * 1. Managing persistent visitor IDs
 * 2. Setting cart attributes that flow through to orders
 * 
 * Note: Experiment assignment is handled by closera-loader.js via PostHog
 */

import { logger } from './logger';

// Storage keys
const VISITOR_ID_KEY = 'closera_visitor_id';
const VISIT_COUNT_KEY = 'closera_visit_count';
const FIRST_VISIT_KEY = 'closera_first_visit';

/**
 * Generate a UUID v4 for visitor identification
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create a persistent visitor ID
 * Uses localStorage on the merchant's domain
 */
export function getOrCreateVisitorId(): string {
  try {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = generateUUID();
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }
    return visitorId;
  } catch {
    // localStorage might be disabled - generate ephemeral ID
    return generateUUID();
  }
}

/**
 * Get visitor info including visit count and return visitor status
 * Increments visit count each time it's called (should be called once per session)
 */
export function getVisitorInfo(): { isReturning: boolean; visitCount: number; firstVisit: string; daysSinceFirstVisit: number } {
  try {
    // Get current visit count and increment
    const currentCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
    const newCount = currentCount + 1;
    localStorage.setItem(VISIT_COUNT_KEY, newCount.toString());
    
    // Get or set first visit timestamp
    let firstVisit = localStorage.getItem(FIRST_VISIT_KEY);
    if (!firstVisit) {
      firstVisit = new Date().toISOString();
      localStorage.setItem(FIRST_VISIT_KEY, firstVisit);
    }
    
    // Calculate days since first visit
    const firstVisitDate = new Date(firstVisit);
    const now = new Date();
    const daysSinceFirstVisit = Math.floor((now.getTime() - firstVisitDate.getTime()) / (1000 * 60 * 60 * 24));
    
    return {
      isReturning: newCount > 1,
      visitCount: newCount,
      firstVisit,
      daysSinceFirstVisit,
    };
  } catch {
    // localStorage might be disabled
    return {
      isReturning: false,
      visitCount: 1,
      firstVisit: new Date().toISOString(),
      daysSinceFirstVisit: 0,
    };
  }
}

/**
 * Set Shopify cart attributes
 * These persist through checkout and appear in order.note_attributes
 * 
 * @param attributes - Key-value pairs to set on the cart
 * @returns Success boolean
 */
export async function setCartAttributes(
  attributes: Record<string, string>
): Promise<boolean> {
  try {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    });

    if (!response.ok) {
      logger.warn('[Closera Attribution] Failed to set cart attributes');
      return false;
    }

    return true;
  } catch {
    // Fail silently - attribution is best-effort, shouldn't break the widget
    logger.warn('[Closera Attribution] Error setting cart attributes');
    return false;
  }
}

/**
 * Update cart attributes with experiment data
 * Call this when experiments are loaded to ensure attribution is set
 * 
 * @param experiments - Object containing all active experiment variants
 * @returns Success boolean
 */
export async function updateCartWithExperiments(
  experiments: Record<string, string | boolean>
): Promise<boolean> {
  const visitorId = getOrCreateVisitorId();
  
  try {
    // Always set visitor ID for attribution, even without experiments
    const attributes: Record<string, string> = {
      closera_visitor_id: visitorId,
      closera_timestamp: new Date().toISOString(),
    };
    
    // Add experiments if we have any
    if (Object.keys(experiments).length > 0) {
      attributes.closera_experiments = JSON.stringify(experiments);
    }
    
    const success = await setCartAttributes(attributes);

    if (success) {
      logger.debug('[Closera Attribution] Updated cart with visitor ID and experiments');
    }

    return success;
  } catch {
    logger.warn('[Closera Attribution] Error updating cart with experiments');
    return false;
  }
}
